import React, { useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import * as Sentry from '@sentry/react';
import {
  ConditionalOptionValues,
  CreditApplicationFields,
  CreditApplicationTemplate,
  InternalFormSubmission,
} from '@dill/dill-shared';
import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import {
  declineInternalForm,
  getInternalFormForFilling,
  getP21Class1CodesForInternalForm,
  submitInternalForm,
} from '../../../../redux/services/internalFormsService';
import { getBasicSupplier } from '../../../../redux/services/buyersService';
import {
  InternalSignatureDocumentFillableFields,
  InternalSignatureDocumentFillableFieldsRef,
} from '../CreditApplicationComponents/InternalSignatureDocumentFillableFields';
import CreateSignatureModal from '../../lienWaivers/supplier/CreateSignatureModal/CreateSignatureModal';
import JobSavingSuccess from '../../../../pages/RequestJobInformationSheet/JobSavingSuccess';
import { DillButton } from '../../../general/Dill';
import { AppInputField } from '../../../general/AppInputField/AppInputField';
import { firebaseLargeFileUploadHelper } from '../../../../utils/helpers';
import dillLogoWithText from '../../../../assets/svg/dill_logo_with_text.svg';
import PublicFormSidebar from '../CreditApplicationComponents/PublicFormSidebar';
import {
  InternalFormSection,
  getInternalFormSections,
  getSectionRowSizes,
  groupSectionsByBorder,
  isInternalFormFieldVisible,
} from './internalFormUtils';

type PublicInternalFormSubmission = Omit<InternalFormSubmission, 'accessToken'>;

// DATE values are kept as Date objects — the date picker can't render a date string (it throws
// "Invalid time value").
const initialValueForField = (field: CreditApplicationFields) => {
  if (field.fromField === 'CHECK_BOX') return false;
  if (field.fromField === 'DATE' && field.defaultValue?.toLowerCase().trim() === 'current') {
    return new Date();
  }
  return null;
};

// A later signer starts from the answers already on the form. Dates are stored as MM-DD-YYYY.
const savedValueForField = (field: CreditApplicationFields, value: any) => {
  if (field.fromField === 'DATE') {
    if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
    if (typeof value === 'string' && value) {
      const parsed = moment(value, ['MM-DD-YYYY', 'MM/DD/YYYY', moment.ISO_8601]);
      return parsed.isValid() ? parsed.toDate() : initialValueForField(field);
    }
    return initialValueForField(field);
  }
  return value ?? initialValueForField(field);
};

const FIELDS_ERROR_MESSAGE =
  'Some fields are missing or invalid. Please check the highlighted fields above.';

const SINGLE_EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Email fields (isFieldEmail) must hold a valid address — or, when set to allow several, only valid
// addresses separated by commas or semicolons. Same check as the credit application form.
const emailErrorFor = (field: CreditApplicationFields, value: any): string => {
  if (field.fromField !== 'TEXT' || !field.isFieldEmail) return '';
  const text = typeof value === 'string' ? value.trim() : '';
  if (!text) return '';
  if ((field.emailValidationType ?? 'SINGLE') === 'MULTIPLE') {
    const invalid = text
      .split(/[,;]/)
      .map((email) => email.trim())
      .filter(Boolean)
      .find((email) => !SINGLE_EMAIL_PATTERN.test(email));
    return invalid ? `"${invalid}" is not a valid email address.` : '';
  }
  return SINGLE_EMAIL_PATTERN.test(text) ? '' : `${field.name || 'This'} must be a valid email.`;
};

const isValueMissing = (field: CreditApplicationFields, value: any) => {
  if (!field.isRequired || field.fromField === 'DOCUMENT_FIELD') return false;
  if (field.fromField === 'CHECK_BOX') return value !== true;
  return value === null || value === undefined || value === '';
};

// Public page reached from the emailed link (see sendInternalFormLinkEmails on the backend). The
// whole form is on one page — no steps, no drafts — and is signed and submitted in one go.
// Later signers (the template's internalFormSigners) open the same page from their own email: the
// answers are filled in already and can be changed, and they can sign or decline.
export const FillInternalForm = () => {
  const dispatch = useAppDispatch();
  const { basicSupplier } = useAppSelector((state) => state.buyers);
  const [linkParams, setLinkParams] = useState({
    internalFormSubmissionId: '',
    token: '',
    email: '',
    domainId: '',
  });
  const [isLoading, setIsLoading] = useState(true);
  const [submission, setSubmission] = useState<PublicInternalFormSubmission | null>(null);
  const [template, setTemplate] = useState<CreditApplicationTemplate | null>(null);
  const [values, setValues] = useState<Record<string, any>>({});
  const [conditionalValues, setConditionalValues] = useState<
    Record<string, ConditionalOptionValues[]>
  >({});
  const [class1Options, setClass1Options] = useState<string[]>([]);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [isSignatureModalOpen, setSignatureModalOpen] = useState(false);
  const [capturedSignature, setCapturedSignature] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [signerLocation, setSignerLocation] = useState('');
  const [signerName, setSignerName] = useState('');
  const [signerTitle, setSignerTitle] = useState('');
  // Shown by the Sign / Submit buttons instead of a toast.
  const [formError, setFormError] = useState('');
  const [signerRole, setSignerRole] = useState<'FIRST' | 'LATER' | null>(null);
  const [currentStepLabel, setCurrentStepLabel] = useState('');
  const [isDeclining, setIsDeclining] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const [resultDetails, setResultDetails] = useState<{
    modalType: 'Success' | 'Error';
    title: string;
    details: string;
  } | null>(null);
  const sectionRefs = useRef<Record<string, InternalSignatureDocumentFillableFieldsRef | null>>({});

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const nextParams = {
      internalFormSubmissionId: params.get('internalFormSubmissionId') ?? '',
      token: params.get('token') ?? '',
      email: params.get('email') ?? '',
      domainId: params.get('domainId') ?? '',
    };
    setLinkParams(nextParams);
    if (nextParams.domainId) {
      dispatch(getBasicSupplier({ supplierDomainId: nextParams.domainId }));
    }
    if (!nextParams.internalFormSubmissionId || !nextParams.token || !nextParams.email) {
      setIsLoading(false);
      setResultDetails({
        modalType: 'Error',
        title: 'Invalid Link',
        details: 'This link is missing information and cannot be used.',
      });
      return;
    }
    (async () => {
      const result = await dispatch(
        getInternalFormForFilling({
          internalFormSubmissionId: nextParams.internalFormSubmissionId,
          token: nextParams.token,
          email: nextParams.email,
        })
      );
      setIsLoading(false);
      if (!result.type.includes('fulfilled')) {
        setResultDetails({
          modalType: 'Error',
          title: 'Unable to Open Form',
          details: (result as any).payload || 'This link is no longer valid.',
        });
        return;
      }
      const data = (result as any).payload?.data;
      const fetchedSubmission: PublicInternalFormSubmission | null =
        data?.internalFormSubmission ?? null;
      const fetchedTemplate: CreditApplicationTemplate | null = data?.template ?? null;
      if (!fetchedSubmission || !fetchedTemplate) {
        setResultDetails({
          modalType: 'Error',
          title: 'Unable to Open Form',
          details: 'This form is no longer available.',
        });
        return;
      }
      const fetchedSignerRole: 'FIRST' | 'LATER' | null = data?.signerRole ?? null;
      if (!fetchedSignerRole) {
        setResultDetails({
          modalType: 'Success',
          title: 'Already Submitted',
          details: 'This form has already been filled in and signed.',
        });
        return;
      }
      setSignerRole(fetchedSignerRole);
      setCurrentStepLabel(data?.currentSignerStepLabel ?? '');
      setSubmission(fetchedSubmission);
      setTemplate(fetchedTemplate);
      const allFields = getInternalFormSections(fetchedTemplate).flatMap((s) => s.fields);
      // Signer steps' own answers (e.g. Internal Information) come back as extra pages too.
      const savedFieldValues = [
        ...fetchedSubmission.fieldValues,
        ...(fetchedSubmission.signerFieldValues ?? []),
      ];
      const saved =
        fetchedSignerRole === 'LATER'
          ? new Map(savedFieldValues.map((field) => [field.id, field]))
          : new Map();
      setValues(
        allFields.reduce<Record<string, any>>((acc, field) => {
          acc[field.id] = saved.has(field.id)
            ? savedValueForField(field, saved.get(field.id)?.value)
            : initialValueForField(field);
          return acc;
        }, {})
      );
      setConditionalValues(
        savedFieldValues.reduce<Record<string, ConditionalOptionValues[]>>((acc, field) => {
          if (fetchedSignerRole === 'LATER' && field.fieldValue) {
            acc[field.id] = field.fieldValue;
          }
          return acc;
        }, {})
      );
      // Only worth the P21 round-trip when the form actually has a Marketing Segment field.
      if (allFields.some((field) => field.id.includes('marketing-segment'))) {
        dispatch(
          getP21Class1CodesForInternalForm({
            internalFormSubmissionId: nextParams.internalFormSubmissionId,
            token: nextParams.token,
            email: nextParams.email,
          })
        ).then((res) => {
          if (res.type.includes('fulfilled')) {
            setClass1Options((res as any).payload?.data?.class1Codes ?? []);
          }
        });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const ipRes = await fetch('https://free.freeipapi.com/api/json/');
        const ipData = await ipRes.json();
        setSignerLocation(`${ipData.cityName}, ${ipData.regionName}, ${ipData.countryName}`);
      } catch (err) {
        console.log(err);
      }
    })();
  }, []);

  const sections = useMemo(() => (template ? getInternalFormSections(template) : []), [template]);

  // Re-evaluated on every change so conditional sections (e.g. "Does this include a name
  // change?" -> Yes) appear and disappear as the form is filled in.
  const fieldsById = useMemo(
    () =>
      sections
        .flatMap((section) => section.fields)
        .reduce<Record<string, CreditApplicationFields>>((acc, field) => {
          acc[field.id] = field;
          return acc;
        }, {}),
    [sections]
  );
  const visibleSections = useMemo(
    () =>
      sections
        .map((section) => ({
          ...section,
          fields: section.fields.filter((field) =>
            isInternalFormFieldVisible(field, values, fieldsById)
          ),
        }))
        .filter((section) => section.fields.length > 0),
    [sections, values, fieldsById]
  );
  const visibleFields = useMemo(
    () => visibleSections.flatMap((section) => section.fields),
    [visibleSections]
  );

  const missingRequiredFields = useMemo(
    () => visibleFields.filter((field) => isValueMissing(field, values[field.id])),
    [visibleFields, values]
  );

  const invalidEmailFields = useMemo(
    () => visibleFields.filter((field) => !!emailErrorFor(field, values[field.id])),
    [visibleFields, values]
  );

  const fieldErrors = useMemo(() => {
    if (!submitAttempted) return {};
    const errors = missingRequiredFields.reduce<Record<string, string>>((acc, field) => {
      acc[field.id] = 'Required';
      return acc;
    }, {});
    invalidEmailFields.forEach((field) => {
      errors[field.id] = emailErrorFor(field, values[field.id]);
    });
    return errors;
  }, [submitAttempted, missingRequiredFields, invalidEmailFields, values]);
  const hasFieldErrors = missingRequiredFields.length > 0 || invalidEmailFields.length > 0;

  const branchOptions = useMemo(
    () =>
      (basicSupplier?.branches ?? [])
        .filter((branch) => branch?.notInCreditApp !== true)
        .map((branch) => ({ name: branch?.branchName ?? '', erpId: branch?.erpId ?? '' })),
    [basicSupplier]
  );

  // Scoped to the sender's branch when known, same as the internal signature document page.
  const salesRepOptions = useMemo(() => {
    const seen = new Set<string>();
    return (basicSupplier?.branches ?? [])
      .filter((branch) => branch?.notInCreditApp !== true)
      .filter((branch) => !submission?.branchId || branch.branchId === submission.branchId)
      .flatMap((branch) => branch?.salesRep ?? [])
      .filter((rep) => {
        const name = rep?.name ?? '';
        if (!name || seen.has(name)) return false;
        seen.add(name);
        return true;
      })
      .map((rep) => ({
        name: rep?.name ?? '',
        erpId: rep?.erpId ?? '',
        isHouseRep: rep?.isHouseRep ?? false,
      }));
  }, [basicSupplier, submission?.branchId]);

  const handleSignNowClick = () => {
    setSubmitAttempted(true);
    setFormError('');
    // The highlighted fields above say what's missing; the line by the buttons points to them.
    if (hasFieldErrors) return;
    const conditionalFieldsValid = Object.values(sectionRefs.current).every(
      (ref) => ref?.validate?.() !== false
    );
    if (!conditionalFieldsValid) {
      setFormError(FIELDS_ERROR_MESSAGE);
      return;
    }
    setSignatureModalOpen(true);
  };

  const uploadFileValue = async (value: any, fieldName: string) => {
    if (!(value instanceof File)) return value;
    // Under creditApplicationsFiles/ because that's the path storage.rules lets an
    // unauthenticated visitor write to.
    const url = await firebaseLargeFileUploadHelper({
      blobUrl: URL.createObjectURL(value),
      fileNameWithExtension: value.name,
      storagePath: `creditApplicationsFiles/internalForms/${
        submission?.supplierId ?? ''
      }/${Date.now()}`,
    });
    if (!url) {
      throw new Error(`Upload failed for ${fieldName}: "${value.name}". Please try again.`);
    }
    return { name: value.name, url, fileType: value.name.split('.').pop() || '' };
  };

  const handleSubmit = async () => {
    if (!capturedSignature || isSubmitting || !submission) return;
    if (!signerName.trim() || !signerTitle.trim()) {
      setFormError('Please enter your name and title to sign.');
      return;
    }
    setIsSubmitting(true);
    try {
      const fieldValues = await Promise.all(
        visibleFields
          .filter((field) => field.fromField !== 'DOCUMENT_FIELD')
          .map(async (field) => {
            let value = await uploadFileValue(values[field.id], field.name);
            if (field.fromField === 'DATE' && value) {
              value = moment(value).format('MM-DD-YYYY');
            }
            const nested = conditionalValues[field.id];
            const fieldValue = nested
              ? await Promise.all(
                  nested.map(async (item) => ({
                    ...item,
                    value: await uploadFileValue(item.value, item.name ?? field.name),
                  }))
                )
              : undefined;
            return { id: field.id, value: value ?? null, ...(fieldValue ? { fieldValue } : {}) };
          })
      );
      const result = await dispatch(
        submitInternalForm({
          internalFormSubmissionId: linkParams.internalFormSubmissionId,
          token: linkParams.token,
          email: linkParams.email,
          signature: capturedSignature,
          location: signerLocation,
          name: signerName.trim(),
          title: signerTitle.trim(),
          fieldValues,
        })
      );
      if (result.type.includes('fulfilled')) {
        const nextStatus = (result as any).payload?.data?.status;
        setResultDetails({
          modalType: 'Success',
          title: signerRole === 'LATER' ? 'Form Signed' : 'Form Submitted',
          details:
            nextStatus === 'AWAITING_SIGNATURES'
              ? 'Thank you. The form has been signed and sent to the next signer.'
              : 'Thank you. The form has been signed and sent to the credit team.',
        });
      } else {
        setFormError((result as any).payload || 'Failed to submit the form.');
      }
    } catch (error: any) {
      Sentry.captureException(error);
      setFormError(error?.message || 'Failed to submit the form.');
    }
    setIsSubmitting(false);
  };

  const handleDecline = async () => {
    if (!declineReason.trim() || isSubmitting) return;
    setIsSubmitting(true);
    const result = await dispatch(
      declineInternalForm({
        internalFormSubmissionId: linkParams.internalFormSubmissionId,
        token: linkParams.token,
        email: linkParams.email,
        reason: declineReason.trim(),
      })
    );
    setIsSubmitting(false);
    if (result.type.includes('fulfilled')) {
      setResultDetails({
        modalType: 'Success',
        title: 'Form Declined',
        details: 'The form has been declined and the sender has been notified.',
      });
    } else {
      setFormError((result as any).payload || 'Failed to decline the form.');
    }
  };

  const previousSignatures = [submission?.signature, ...(submission?.signatures ?? [])].filter(
    (signature): signature is NonNullable<typeof signature> => !!signature
  );

  const title = submission?.templateName || template?.name || 'Internal Form';

  return (
    <div className="w-full GREY_100-BG min-h-full flex flex-col md:flex-row">
      <CreateSignatureModal
        open={isSignatureModalOpen}
        handleClose={() => setSignatureModalOpen(false)}
        addSignature={(signature) => {
          if (!signature) return;
          setCapturedSignature(signature);
          setSignatureModalOpen(false);
        }}
        onDownloadRealease={() => {}}
        defaultIsManualSign={false}
        title={`Sign ${title}`}
        subText={`Confirm your name & signature to sign off on this ${title.toLowerCase()}.`}
      />
      {linkParams.domainId && (
        <PublicFormSidebar supplier={basicSupplier} isLoading={!basicSupplier && !resultDetails} />
      )}
      <div className="flex flex-col flex-1 min-w-0">
        <div className="flex items-center justify-between px-6 md:px-10 py-4 WHITE-BG border-b GREY_200-BORDER shadow-sm">
          <h1 className="text-lg md:text-xl font-bold GREY_900-CLR truncate mr-4">{title}</h1>
          <a
            href="https://usedill.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex-shrink-0 flex items-center gap-1.5 hover:opacity-80 transition-opacity">
            <span className="text-xs GREY_500-CLR whitespace-nowrap">Powered by</span>
            <img className="h-7 md:h-9" src={dillLogoWithText} alt="Dill" />
          </a>
        </div>
        {isLoading ? (
          <div className="flex items-center justify-center w-full min-h-[60vh]">
            <p className="text-sm GREY_500-CLR">Loading…</p>
          </div>
        ) : resultDetails ? (
          <div className="flex flex-col items-center w-full justify-center">
            <div className="w-[calc(100%-30px)] md:w-[512px]">
              <JobSavingSuccess
                type={resultDetails.modalType}
                details={resultDetails.details}
                title={resultDetails.title}
              />
            </div>
          </div>
        ) : (
          <div className="flex flex-col px-2 md:px-5 py-4 w-full items-center">
            <div className="w-full md:w-2/3 flex flex-col gap-4">
              {signerRole === 'LATER' && (
                <div className="bg-white p-4 rounded-xl border border-gray-200 flex flex-col gap-2">
                  <p className="text-sm font-semibold GREY_900-CLR">
                    {`Your signature is needed${currentStepLabel ? ` as ${currentStepLabel}` : ''}`}
                  </p>
                  <p className="text-xs GREY_500-CLR">
                    Review the answers below, change anything that needs correcting, then sign — or
                    decline with a reason.
                  </p>
                </div>
              )}
              {/* Every section in one card — the form's title is already in the header. No gap
                  between sections: each field's own py-2 already puts 16px between rows. */}
              <div className="bg-white p-4 rounded-xl border border-gray-200 flex flex-col">
                {groupSectionsByBorder(visibleSections).map((run) => {
                  const renderSection = (section: InternalFormSection) => (
                    <div key={section.key} className="flex flex-col gap-2">
                      {section.title && (
                        <span className="text-sm font-semibold GREY_800-CLR">{section.title}</span>
                      )}
                      <InternalSignatureDocumentFillableFields
                        ref={(el) => {
                          sectionRefs.current[section.key] = el;
                        }}
                        fields={section.fields}
                        values={values}
                        fieldValues={conditionalValues}
                        groupSizes={getSectionRowSizes(section.fields.length, section.columnType)}
                        onChange={(fieldId, value, fieldValue) => {
                          setValues((prev) => ({ ...prev, [fieldId]: value }));
                          if (fieldValue !== undefined) {
                            setConditionalValues((prev) => ({ ...prev, [fieldId]: fieldValue }));
                          }
                        }}
                        errors={fieldErrors}
                        branches={branchOptions}
                        salesReps={salesRepOptions}
                        class1Options={class1Options}
                        bordered={false}
                        addressAutocomplete
                        currencyFormatting
                      />
                    </div>
                  );
                  // Sections in the same borderId group share one bordered box, like the
                  // template preview and the credit application form.
                  return run.borderGroup ? (
                    <div
                      key={run.borderGroup}
                      className="border border-gray-200 rounded-lg px-2 my-2 flex flex-col">
                      {run.sections.map(renderSection)}
                    </div>
                  ) : (
                    run.sections.map(renderSection)
                  );
                })}
              </div>
              {signerRole === 'LATER' && previousSignatures.length > 0 && (
                <div className="bg-white p-4 rounded-xl border border-gray-200 flex flex-col gap-2">
                  <p className="text-sm font-semibold GREY_900-CLR">Signed by</p>
                  {previousSignatures.map((signature, index) => (
                    <div
                      key={`${signature.signedByEmail}-${index}`}
                      className="flex items-center gap-3 border-t border-gray-100 pt-2">
                      <img
                        src={signature.signature}
                        alt="Signature"
                        className="h-6 w-auto object-contain"
                      />
                      <div className="text-xs text-gray-600">
                        <p>
                          {[signature.signedByName, signature.signedByTitle]
                            .filter(Boolean)
                            .join(', ') || signature.signedByEmail}
                          {signature.stepLabel ? ` (${signature.stepLabel})` : ''}
                        </p>
                        <p className="text-[10px] text-gray-400">
                          {`Signed ${moment(signature.dateSigned).format('MM/DD/YYYY hh:mm A')}`}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <div className="bg-white p-4 rounded-xl border border-gray-200 flex flex-col gap-3">
                <div className="flex flex-col border border-gray-200 rounded-xl overflow-hidden">
                  <div className="flex justify-end gap-2 px-2 pt-2">
                    {capturedSignature && (
                      <DillButton
                        label="Clear"
                        variant="secondary"
                        height={28}
                        isDisabled={isSubmitting}
                        onClick={() => setCapturedSignature('')}
                      />
                    )}
                    <DillButton
                      label={capturedSignature ? 'Change Signature' : 'Sign Now'}
                      variant={capturedSignature ? 'secondary' : 'primary'}
                      height={28}
                      isDisabled={isSubmitting}
                      onClick={handleSignNowClick}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2 p-1">
                    <div className="flex flex-col items-start gap-1 p-2">
                      <span className="text-xs font-semibold text-gray-700">Signed by</span>
                      <span className="text-xs text-gray-500">{linkParams.email}</span>
                    </div>
                    <div className="flex flex-col items-start gap-1 p-2">
                      {capturedSignature && (
                        <>
                          <div className="flex items-center gap-2">
                            <img
                              src={capturedSignature}
                              alt="Signature preview"
                              className="h-6 w-auto object-contain"
                            />
                            <p className="text-[9px] text-gray-400">E-signed using Dill</p>
                          </div>
                          <div className="text-[10px] text-gray-500">
                            {`Date Signed: ${moment(new Date()).format('MM/DD/YYYY hh:mm A')}`}
                            {signerLocation && ` at ${signerLocation}`}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 px-3 pb-3">
                    <AppInputField
                      label="Name"
                      isRequired
                      value={signerName}
                      onTextChange={(text: string) => setSignerName(text)}
                    />
                    <AppInputField
                      label="Title"
                      isRequired
                      value={signerTitle}
                      onTextChange={(text: string) => setSignerTitle(text)}
                    />
                  </div>
                </div>
                {isDeclining && (
                  <div className="flex flex-col gap-2 border-t border-gray-100 pt-3">
                    <label className="text-xs font-semibold text-gray-700" htmlFor="decline-reason">
                      Reason for declining
                    </label>
                    <textarea
                      id="decline-reason"
                      className="border border-gray-300 rounded-lg p-2 text-sm outline-none focus:border-blue-400"
                      rows={3}
                      value={declineReason}
                      onChange={(e) => setDeclineReason(e.target.value)}
                    />
                  </div>
                )}
                {(formError || (submitAttempted && hasFieldErrors)) && (
                  <p className="text-sm text-red-600 text-center">
                    {formError || FIELDS_ERROR_MESSAGE}
                  </p>
                )}
                <div className="flex justify-center gap-2 border-t border-gray-100 pt-3">
                  {signerRole === 'LATER' &&
                    (isDeclining ? (
                      <>
                        <DillButton
                          label="Cancel"
                          variant="secondary"
                          isDisabled={isSubmitting}
                          onClick={() => {
                            setIsDeclining(false);
                            setDeclineReason('');
                          }}
                        />
                        <DillButton
                          label="Confirm Decline"
                          variant="primary"
                          isDisabled={!declineReason.trim() || isSubmitting}
                          isLoading={isSubmitting}
                          onClick={handleDecline}
                        />
                      </>
                    ) : (
                      <DillButton
                        label="Decline"
                        variant="secondary"
                        isDisabled={isSubmitting}
                        onClick={() => setIsDeclining(true)}
                      />
                    ))}
                  {!isDeclining && (
                    <DillButton
                      label="Submit"
                      variant="primary"
                      centered
                      isDisabled={
                        !capturedSignature ||
                        !signerName.trim() ||
                        !signerTitle.trim() ||
                        isSubmitting
                      }
                      isLoading={isSubmitting}
                      onClick={handleSubmit}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FillInternalForm;
